import { create } from "zustand"
import { persist } from "zustand/middleware"
import { apiService } from "@/lib/api"
import {AUTH_API} from "@/constants/api-paths"
import {
  LoginInRequest,
  TokenResponse,
  StorageService, Self
} from "@/models/auth"

// ===== Auth State Interface =====
interface AuthState {
  admin: Self | null
  tokens: TokenResponse | null
  loading: boolean
  error: string | null

  // 액션
  logIn: (user: LoginInRequest) => Promise<{ success: boolean; tokens?: TokenResponse }>
  logInWithWorks: (data:any) => Promise<{ success: boolean; tokens?: TokenResponse }>
  logOut: () => Promise<{ success: boolean }>
  getSelf: () => Promise<{ data: Self }>
  setAdmin: (admin: Self) => void
  clearError: () => void
}

// ===== Zustand Store =====
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 초기 상태
      admin: null,
      tokens: null,
      loading: false,
      error: null,

      // 로그인
      logIn: async (user: LoginInRequest) => {
        set({ loading: true, error: null })
        try {
          const response = await apiService.post<{ accessToken: string }>(AUTH_API.LOGIN, user)
          const tokens = {
            access_token: response.accessToken,
          }
          // 토큰 저장
          StorageService.setTokens(tokens.access_token)

          // getSelf 호출하여 관리자 정보 가져오기
          try {
            const selfResult = await get().getSelf()
            get().setAdmin(selfResult.data)
            return { success: true, tokens }
          } catch (error) {
            console.error('getSelf 실패:', error)
            return { success: false }
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Login API 오류'
          set({ error: errorMsg })
          return { success: false }
        } finally {
          set({ loading: false })
        }
      },

      // 네이버 웍스 로그인
      logInWithWorks: async (data:any) => {
        set({ loading: true, error: null })
        try {
          const response = await apiService.post<{ accessToken: string }>(AUTH_API.NAVER_WORKS, data)
          const tokens = {
            access_token: response.accessToken,
          }
          // 토큰 저장
          StorageService.setTokens(tokens.access_token)

          // getSelf 호출하여 관리자 정보 가져오기
          try {
            const selfResult = await get().getSelf()
            get().setAdmin(selfResult.data)
            return { success: true, tokens }
          } catch (error) {
            console.error('[logInWithWorks] getSelf 실패:', error)
            return { success: false }
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Works Login API 오류'
          set({ error: errorMsg })
          return { success: false }
        } finally {
          set({ loading: false })
        }
      },

      // 로그아웃
      logOut: async () => {
        try {
          StorageService.clearTokens()
          set({
            admin: null,
            tokens: null,
            error: null
          })
          return { success: true }
        } catch (error) {
          console.error('signOut 오류:', error)
          return { success: false }
        }
      },

      // 관리자 자신 조회
      getSelf: async () => {
        set({ loading: true, error: null })
        try {
          const data = await apiService.get<Self>(AUTH_API.SELF)
          return { data }
        } catch (error) {
          const msg = error instanceof Error ? error.message : '조회에 실패했습니다.'
          set({ error: msg })
          throw error
        } finally {
          set({ loading: false })
        }
      },


      // 관리자 정보 설정
      setAdmin: (admin) => set({ admin }),

      // 에러 초기화
      clearError: () => set({ error: null })
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        admin: state.admin,
        tokens: state.tokens,
      }),
    }
  )
)